import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpClientService } from '../../shared/services/http-client/http-client.service';
import { getInstitutoStatusLabel } from '../constants/instituto-previdenciario-opcoes';
import { extractIsoDate } from '../utils/date-format.util';
import {
  InstitutoFiltros,
  InstitutoLista,
  InstitutoPrevidenciario,
  InstitutoPrevidenciarioDetalhe,
} from '../models/instituto-previdenciario.model';
import {
  AvaliacaoDocumentoRequestDto,
  AvaliarDocumentosRequestDto,
  InstitutoPrevidenciarioDetalheResponseDto,
  InstitutoPrevidenciarioListResponseDto,
  InstitutoPrevidenciarioParticipanteDto,
} from '../api/models';

const ADMIN_BASE_PATH = '/v1/admin';

/** Status exibido quando a API não manda nenhum. */
const STATUS_PADRAO = 'Pendente';

@Injectable({
  providedIn: 'root',
})
export class InstitutoPrevidenciarioService {
  constructor(private readonly httpClient: HttpClientService) {}

  /**
   * Cadastros prévios da API Instituto Online: totais e solicitações na raiz da resposta. Os filtros
   * preenchidos vão como query params; os vazios ficam de fora.
   */
  listarSolicitacoes(filtros?: InstitutoFiltros): Observable<InstitutoLista> {
    return this.httpClient
      .get<InstitutoPrevidenciarioListResponseDto>(`${ADMIN_BASE_PATH}/cadastro-previo`, {
        params: this.toQueryParams(filtros),
        apiUrl: environment.apiInstitutoPrevidenciarioUrl,
      })
      .pipe(map((response) => this.mapLista(response)));
  }

  /** Detalhe de uma solicitação, com o histórico de status, pelo `idSolicitacao` da lista. */
  obterSolicitacao(idSolicitacao: number | string): Observable<InstitutoPrevidenciarioDetalhe> {
    return this.httpClient
      .get<InstitutoPrevidenciarioDetalheResponseDto>(
        `${ADMIN_BASE_PATH}/cadastro-previo/obterporidsolicitacao/${encodeURIComponent(idSolicitacao)}`,
        { apiUrl: environment.apiInstitutoPrevidenciarioUrl },
      )
      .pipe(map((dto) => this.mapDetalhe(dto)));
  }

  /**
   * Envia a avaliação dos documentos da solicitação numa chamada só. A lista é montada pelo
   * componente: todos como `APROVADO` na aprovação, ou os marcados como `REPROVADO` (com motivo) no
   * pedido de reenvio.
   */
  avaliarDocumentos(
    idSolicitacao: number | string,
    documentos: AvaliacaoDocumentoRequestDto[],
  ): Observable<void> {
    const body: AvaliarDocumentosRequestDto = { documentos };

    return this.httpClient.post<void>(
      `${ADMIN_BASE_PATH}/solicitacoes/${encodeURIComponent(idSolicitacao)}/documentos/avaliar`,
      body,
      { apiUrl: environment.apiInstitutoPrevidenciarioUrl },
    );
  }

  /** Só os filtros preenchidos; números vão como texto (`plano=23`). */
  private toQueryParams(filtros: InstitutoFiltros = {}): HttpParams {
    return Object.entries(filtros).reduce(
      (params, [chave, valor]) =>
        valor == null || valor === '' ? params : params.set(chave, String(valor)),
      new HttpParams(),
    );
  }

  /** Totais ausentes viram 0; sem `solicitacoes`, a lista fica vazia. */
  private mapLista(dto?: InstitutoPrevidenciarioListResponseDto | null): InstitutoLista {
    return {
      totalAdesoes: dto?.totalAdesoes ?? 0,
      qtdPorAutopatrocinio: dto?.qtdPorAutopatrocinio ?? 0,
      qtdPorBpd: dto?.qtdPorBpd ?? 0,
      qtdPorResgate: dto?.qtdPorResgate ?? 0,
      qtdPorPortabilidade: dto?.qtdPorPortabilidade ?? 0,
      solicitacoes: (dto?.solicitacoes ?? []).map((item) => this.mapParticipante(item)),
    };
  }

  /**
   * `plano` fica com o id da API (o mesmo de `INSTITUTO_PLANOS`). `dataSolicitacao` fica só com a
   * data (sem hora), que é o que `formatDateIsoToBr` espera na tela. `statusUnificado` é o que a
   * lista e o gráfico mostram: o código da API (`EmRetencao`) vira a label de `INSTITUTO_STATUS`
   * ("Em retenção") e um código fora da lista fica como veio; sem ele (a API manda `null` em alguns
   * status, como `AGUARDANDO_ASSINATURA_TERMO`), fica "Pendente".
   */
  private mapParticipante(dto: InstitutoPrevidenciarioParticipanteDto): InstitutoPrevidenciario {
    return {
      idSolicitacao: dto.idSolicitacao,
      nomeParticipante: dto.nomeParticipante ?? '',
      matricula: Number(dto.matricula) || 0,
      plano: dto.plano ?? 0,
      tipoInstituto: dto.tipoInstituto ?? '',
      dataSolicitacao: extractIsoDate(dto.dataSolicitacao) || null,
      statusUnificado: getInstitutoStatusLabel(dto.statusUnificado) || STATUS_PADRAO,
    };
  }

  /**
   * `dataSolicitacao` mantém a hora (o detalhe mostra data e hora); `ultimaAtualizacao` fica só com
   * a data, como em `mapParticipante`. `status` fica como vem da API; vazio cai em "Pendente". No
   * histórico, o `statusUnificado` vira a label de `INSTITUTO_STATUS`, como na lista (vazio sem ele).
   */
  private mapDetalhe(dto: InstitutoPrevidenciarioDetalheResponseDto): InstitutoPrevidenciarioDetalhe {
    return {
      protocolo: dto.protocolo ?? null,
      nomeParticipante: dto.nomeParticipante ?? '',
      matricula: dto.matricula ?? null,
      plano: dto.plano ?? null,
      tipoInstituto: dto.tipoInstituto ?? null,
      dataSolicitacao: dto.dataSolicitacao || null,
      status: dto.status?.trim() || STATUS_PADRAO,
      elegivelRetencao: dto.elegivelRetencao ?? null,
      ultimaAtualizacao: extractIsoDate(dto.ultimaAtualizacao) || null,
      podeAvaliarDocs: dto.podeAvaliarDocs ?? false,
      historico: (dto.historico ?? []).map((item) => ({
        idHistorico: item.idHistorico,
        statusAnterior: item.statusAnterior ?? null,
        statusNovo: item.statusNovo ?? null,
        observacao: item.observacao ?? null,
        alteradoPor: item.alteradoPor ?? null,
        alteradoEm: item.alteradoEm ?? null,
        statusUnificado: getInstitutoStatusLabel(item.statusUnificado),
      })),
      // Sem link não há como baixar: o documento não entra na lista.
      docs: (dto.docs ?? [])
        .filter((doc) => !!doc.link)
        .map((doc) => ({
          idDocumento: doc.idDocumento,
          nomeArquivo: doc.nomeArquivo ?? '',
          tipoDocumento: doc.tipoDocumento ?? '',
          enviadoEm: doc.enviadoEm ?? '',
          link: doc.link!,
          statusAvaliacao: doc.statusAvaliacao ?? '',
          avaliacoes: (doc.avaliacoes ?? []).map((avaliacao) => ({
            resultado: avaliacao.resultado ?? '',
            motivo: avaliacao.motivo ?? '',
            avaliadoPor: avaliacao.avaliadoPor ?? '',
            avaliadoEm: avaliacao.avaliadoEm ?? '',
          })),
        })),
    };
  }
}
